import * as d3 from 'd3-selection'
import intersection from 'path-intersection'

/**
 * Rotates a point around a given center by a specified angle.
 *
 * @param {Array} point The point to rotate, as [x, y].
 * @param {Array} center The center of rotation, as [x, y].
 * @param {number} angle The rotation angle in degrees.
 * @returns {Array} The rotated point as [xNew, yNew].
 */
function rotatePoint (point, center, angle) {
  const radians = angle * Math.PI / 180

  // Calculate cosine and sine of the angle for rotation
  const cos = Math.cos(radians)
  const sin = Math.sin(radians)

  // Destructure the point coordinates
  let [x, y] = point

  // Translate point to origin (make center as origin)
  x -= center[0]
  y -= center[1]

  // Rotate point around the origin
  const xNew = x * cos - y * sin
  const yNew = x * sin + y * cos

  // Translate point back to original position
  return [xNew + center[0], yNew + center[1]]
}

/**
 * Calculates the top-left corner of a bounding box after rotation.
 * This is used to determine the bounding box of a rotated SVG shape.
 *
 * @param {Object} dimensions An object with width, height, center, and angle properties.
 * @returns {Array} The top-left corner of the bounding box as [x, y] in rotated space.
 */
function getTopLeft (dimensions) {
  let minX = Number.POSITIVE_INFINITY
  let minY = Number.POSITIVE_INFINITY

  // Define the unrotated bounding box corners
  const points = [
    [0, 0], // Top-left
    [dimensions.width, 0], // Top-right
    [dimensions.width, dimensions.height], // Bottom-right
    [0, dimensions.height] // Bottom-left
  ]

  // Rotate each corner to find the new bounding box
  points.forEach(point => {
    let [rotatedX, rotatedY] = rotatePoint(point, dimensions.center, dimensions.angle)

    // Update minX and minY based on rotated points
    minX = Math.min(minX, rotatedX)
    minY = Math.min(minY, rotatedY)
  })

  // Return the top-left corner of the bounding box in rotated space
  return rotatePoint([minX, minY], dimensions.center, 360 - dimensions.angle)
}

/*
 * Returns the size of the badge based on the size of the shape, but bounded
 * by the limits.
 */
function size ({width, height}) {
  const base = {
    width: 150, // based on a 150x100 shape
    height: 100,
    size: 20, // we'd use a 20x20 badge
  }

  const limits = {
    min: 15,
    max: 40,
  }

  const scale = Math.min(width / base.width, height / base.height)
	const scaled = scale * base.size

	// Respect the limits
	return Math.max(Math.min(scaled, limits.max), limits.min)
}

/*
 * Renders the badges for shape. Badges should always be on
 * top of the shape, but below handles.
 */
export default function (selection) {
  selection.each(function (d) {
    // Add the badges group
    d3.select(this).selectAll('g.badges')
      .data(d => {
        return [{
          badges: d.badges || [],
          paths: d.paths,
          dimensions: d.dimensions,
        }]
      })
      .join('g')
      .attr('class', 'badges')
      .selectAll('.badge')
      .data(d => {
        if (!d.badges.length) {
          return []
        }
        // Determine how to position this badge
        // Build a vector starting from the center to the top left of the symbol
        const dimensions = d.dimensions
        const topLeft = getTopLeft(dimensions)

        // Assume if there are > 1 paths, all shapes share a transform
        let translate = d.paths[0].translate || [0, 0]
        // Make a vector from top left to center, but offseting by the translate
        // on the source shape. This is b/c of symbols like the callout

        const pt1 = [dimensions.center[0] - translate[0], dimensions.center[1] - translate[1]]
        const pt2 = [topLeft[0] - translate[0], topLeft[1] - translate[1]]
        // Build a path from pt1 to pt2
        const path = `M${pt1[0]},${pt1[1]}L${pt2[0]},${pt2[1]}`

        // Find the intersection points between our vector and the shape's paths.
        const intersections = [].concat(...d.paths.map(component => {
          return intersection(path, component.path)
        }))

        // Find the intersection point that is closest to the end point
        const point = intersections.sort((a, b) => {
          const d1 = Math.sqrt((a.x - pt2[0]) ** 2 + (a.y - pt2[1]) ** 2)
          const d2 = Math.sqrt((b.x - pt2[0]) ** 2 + (b.y - pt2[1]) ** 2)
          return d1 - d2
        }).at(0) || {x: dimensions.center[0], y: dimensions.center[1]} // Set to center if no intersections

        // Translate the point back to the original coordinate system
        point.x += translate[0]
        point.y += translate[1]


        // If the point isn't at the center, we need to adjust the point so it's 3% closer to the top left
        if (point.x !== dimensions.center[0] || point.y !== dimensions.center[1]) {
          const v = [point.x - topLeft[0], point.y - topLeft[1]] // vector from top left to point
          const distance = Math.sqrt(v[0] ** 2 + v[1] ** 2) || Number.MIN_VALUE // distance from top left to point, preventing a distance of 0
          const newDistance = distance * 0.97 // Reduce the distance by 3%
          const direction = [v[0] / distance, v[1] / distance] // Normalize the vector
          const newV = [direction[0] * newDistance, direction[1] * newDistance] // new vector

           // adjust the intersection point using the new vector
          point.x = topLeft[0] + newV[0]
          point.y = topLeft[1] + newV[1]
        }

        return d.badges.map(badge => {
          return {
            rotation: dimensions.angle,
            id: badge,
            point: [point.x, point.y],
            size: size(dimensions),
          }
        })
      })
      .join('use')
      .attr('class', 'badge')
      .attr('xlink:href', d => `#${d.id}`)
      .attr('x', d => d.point[0] - d.size / 2)
      .attr('y', d => d.point[1] - d.size / 2)
      .attr('width', d => d.size)
      .attr('height', d => d.size)
      .attr('transform', d => {
        return `rotate(${-d.rotation} ${d.point[0]} ${d.point[1]})`
      })
  })
}
